import { Row } from '@tanstack/react-table'
import { EllipsisVertical } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { userColumnSchema } from '../_data/user-interface'
import { Link } from 'react-router-dom'
import { Separator } from '@/components/ui/separator'
import { UserStatus } from '../user-details/_data/user-details-interface'
import DeleteUserModal from './delete-user-modal'
import { useUser } from '@/pages/enterprise/user-provider'
import { useState } from 'react'
import ResendInvitationModal from './resend-invitation-modal'
import ActivateUserModal from './activate-user-modal'
import { useQueryClient } from '@tanstack/react-query'
import { getOrganizationUsersOrganizationOrganizationIdUsersGetQueryKey } from '@/generated/@tanstack/react-query.gen'
import { cn } from '@/lib/utils'
import {
  TooltipTrigger,
  TooltipContent,
  Tooltip,
} from '@/components/ui/tooltip'

interface DataTableRowActionsProps<TData> {
  row: Row<TData>
}

export function UserTableAction<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const values = userColumnSchema.parse(row.original)
  const { organizationId, availableSeats } = useUser()

  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openReInvitationModal, setOpenReInvitationModal] = useState(false)
  const [openActivateModal, setOpenActivateModal] = useState(false)

  const isActive = values.status.activation === UserStatus.Active
  const isInvited = values.status.activation === UserStatus.Invited
  const isDeactivated = values.status.activation === UserStatus.Deactivated

  const queryClient = useQueryClient()

  const onValidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: getOrganizationUsersOrganizationOrganizationIdUsersGetQueryKey({
        path: { organization_id: organizationId },
      }),
    })
  }

  const isCurrentUser = values.id === useUser().id

  return (
    <div className="flex item-center justify-center">
      <div className="flex flex-row items-center border border-[#D5D5D5] rounded-lg h-8 justify-between">
        <div className="flex-1 w-[48px]">
          <Tooltip>
            <TooltipTrigger className="w-full">
              <Link
                className="flex items-center justify-center w-full"
                to={`/enterprise/user/${values.id}`}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g opacity="0.6">
                    <path
                      d="M21.544 11.045C21.848 11.471 22 11.685 22 12C22 12.316 21.848 12.529 21.544 12.955C20.178 14.871 16.689 19 12 19C7.31 19 3.822 14.87 2.456 12.955C2.152 12.529 2 12.315 2 12C2 11.684 2.152 11.471 2.456 11.045C3.822 9.129 7.311 5 12 5C16.69 5 20.178 9.13 21.544 11.045Z"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9C11.2044 9 10.4413 9.31607 9.87868 9.87868C9.31607 10.4413 9 11.2044 9 12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12Z"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </Link>
            </TooltipTrigger>
            <TooltipContent className="bg-[#000000] text-[12px] text-white">
              View Profile
            </TooltipContent>
          </Tooltip>
        </div>
        <Separator
          orientation="vertical"
          className="h-8 w-[0.6px] bg-[#D5D5D5]"
        />
        <div className="flex-1 w-[48px]">
          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={isCurrentUser}
              className={cn(
                'flex items-center justify-center w-full px-2 !py-0',
                {
                  'cursor-not-allowed pointer-events-none': isCurrentUser,
                }
              )}
            >
              <EllipsisVertical className={'w-5 h-5'} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[174px]">
              {isInvited && (
                <DropdownMenuItem
                  disabled={
                    values.invitationValid === false && availableSeats === 0
                  }
                  onClick={() => setOpenReInvitationModal(true)}
                  className={cn('w-full cursor-pointer')}
                >
                  Resend Invitation
                </DropdownMenuItem>
              )}
              {isActive && (
                <DropdownMenuItem
                  onClick={() => setOpenActivateModal(true)}
                  className={'w-full cursor-pointer'}
                >
                  Deactivate
                </DropdownMenuItem>
              )}
              {isDeactivated && (
                <DropdownMenuItem
                  onClick={() => setOpenActivateModal(true)}
                  className={'w-full cursor-pointer'}
                >
                  Activate
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setOpenDeleteModal(true)}
                className={'w-full text-destructive cursor-pointer'}
              >
                Delete User
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ActivateUserModal
        email={values.email}
        onStatusChanged={() => onValidateQuery()}
        isActivated={isActive}
        isHiddenTrigger={true}
        open={openActivateModal}
        setOpen={setOpenActivateModal}
        organizationId={organizationId}
        userId={values.id}
      />
      <ResendInvitationModal
        email={values.email}
        isHiddenTrigger={true}
        open={openReInvitationModal}
        setOpen={setOpenReInvitationModal}
        organizationId={organizationId}
        userId={values.id}
      />
      <DeleteUserModal
        onDeleted={() => onValidateQuery()}
        isHiddenTrigger={true}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        organizationId={organizationId}
        userId={values.id}
      />
    </div>
  )
}
